import { createFileRoute, redirect, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { useSession } from "@/hooks/use-session";
import { apiSendJson, setStoredTenantId, clientRequestHeaders } from "@/lib/sm-api";
import { Check, ChevronRight, Loader2, MapPin, Package, Settings2, ShoppingBasket, Store } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { HelpLink } from "@/components/help/HelpLink";
import { PageLoader } from "@/components/PageLoader";
import { FM_BUSINESS_PRESETS, type FmBusinessPresetId } from "@/lib/business-presets";
import {
  BRANCH_TYPE_DESCRIPTIONS,
  BRANCH_TYPE_LABELS,
  branchTypesForBusinessProfile,
  type BranchTypeKey,
  type BusinessProfile,
} from "@/lib/business-profile";
import { NormalizedAddressForm } from "@/components/address/NormalizedAddressForm";
import {
  EMPTY_NORMALIZED_ADDRESS,
  formatNormalizedAddressLine,
  normalizeArPostalCode,
  validateNormalizedAddress,
  type NormalizedAddress,
} from "@structura/core/address";

export const Route = createFileRoute("/app/onboarding")({
  beforeLoad: async () => {
    try {
      const res = await fetch("/api/platform-flags", { credentials: "include" });
      if (res.ok) {
        const data = (await res.json()) as { flags?: { onboarding_online_enabled?: boolean } };
        if (data.flags?.onboarding_online_enabled === false) {
          // Solo salir si ya hay workspace. Sin tenantId, app.tsx manda acá:
          // redirigir a /app/dashboard pelea con ese gate → commitLocation infinito.
          const session = await fetch("/api/auth/session", { credentials: "include" })
            .then((r) => (r.ok ? r.json() as Promise<{ user?: { tenantId?: string | null } }> : null))
            .catch(() => null);
          if (session?.user?.tenantId) {
            throw redirect({ to: "/app/dashboard" });
          }
        }
      }
    } catch (e) {
      if (e && typeof e === 'object' && 'to' in (e as object)) throw e;
    }
  },
  component: OnboardingPage,
  head: () => ({ meta: [{ title: "Configuración inicial — Structura Market" }] }),
});

type Step = 0 | 1 | 2 | 3 | 4;

const STEP_LABELS = [
  { label: "Negocio",      icon: Store },
  { label: "Sucursal",     icon: MapPin },
  { label: "Categorías",   icon: Package },
  { label: "Productos",    icon: ShoppingBasket },
  { label: "Pagos",        icon: Settings2 },
] as const;

// ─── Step forms ──────────────────────────────────────────────────────────────

interface NegocioData {
  name: string;
  description: string;
  whatsappNumber: string;
  branchType: BranchTypeKey;
  showAddressOnStorefront: boolean;
}

interface SucursalData {
  name: string;
  deliveryDomicilio: boolean;
  deliveryRetiro: boolean;
  phone: string;
}

interface CategoriaData { name: string; }

interface ProductoData {
  name: string;
  description: string;
  price: string;
  tipoVenta: "unidad" | "peso";
  stock: string;
}

interface PagosData {
  mpPublicKey: string;
  mpAccessToken: string;
  efectivo: boolean;
  transferencia: boolean;
}

function StepIndicator({ step, current }: { step: number; current: Step }) {
  const done   = step < current;
  const active = step === current;
  const s      = STEP_LABELS[step];
  return (
    <div className={cn(
      "flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium",
      active  ? "border-primary bg-primary/10 text-primary"
      : done  ? "border-green-500/40 bg-green-500/5 text-green-500"
              : "border-border text-muted-foreground",
    )}>
      {done ? <Check className="h-3 w-3" /> : <span>{step + 1}</span>}
      {s.label}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

function OnboardingPage() {
  const navigate = useNavigate();
  const { user, refresh } = useSession();

  // Si el usuario ya tiene tenant, saltar al paso donde quedó (re-entrada).
  const existingTenantStep: Step = user?.tenantId
    ? (Math.min(4, Math.max(1, (user as { onboardingStep?: number }).onboardingStep ?? 1)) as Step)
    : 0;

  const [step, setStep] = useState<Step>(existingTenantStep);
  const [busy, setBusy] = useState(false);

  // Data per step
  const [negocio, setNegocio]     = useState<NegocioData>({
    name: "",
    description: "",
    whatsappNumber: "",
    branchType: "store",
    showAddressOnStorefront: true,
  });
  const [geoAddress, setGeoAddress] = useState<NormalizedAddress>({ ...EMPTY_NORMALIZED_ADDRESS });
  const [businessPresetId, setBusinessPresetId] = useState<FmBusinessPresetId>("almacen");
  const [sucursal, setSucursal]   = useState<SucursalData>({ name: "Principal", deliveryDomicilio: true, deliveryRetiro: true, phone: "" });
  const [primaryLocationId, setPrimaryLocationId] = useState<string | null>(null);
  const [categorias, setCategorias] = useState<CategoriaData[]>([{ name: "" }]);
  const [productos, setProductos]   = useState<ProductoData[]>([{ name: "", description: "", price: "", tipoVenta: "unidad", stock: "0" }]);
  const [pagos, setPagos]           = useState<PagosData>({ mpPublicKey: "", mpAccessToken: "", efectivo: true, transferencia: true });

  const [tenantId, setTenantId] = useState<string | null>(user?.tenantId ?? null);
  const [tenantSlug, setTenantSlug] = useState<string>("");

  const authHeader = () => {
    return clientRequestHeaders();
  };

  const tidHeader = (tid: string) => ({
    ...authHeader(),
    "X-Tenant-Id": tid,
    "Content-Type": "application/json",
  });

  const selectedPreset = FM_BUSINESS_PRESETS.find((p) => p.id === businessPresetId);
  const businessProfile: BusinessProfile = selectedPreset?.profile ?? "market";
  const isDigitalOnboarding = businessProfile === "digital_products";
  const allowedBranchTypes = branchTypesForBusinessProfile(businessProfile);

  const addressLineFromGeo = () =>
    formatNormalizedAddressLine({
      ...geoAddress,
      postalCode: normalizeArPostalCode(geoAddress.postalCode),
    }) || geoAddress.formattedAddress.trim();

  const mapsUrlFromGeo = (lat: number, lng: number, placeId?: string | null) => {
    if (placeId?.trim()) {
      return `https://www.google.com/maps/search/?api=1&query=Google&query_place_id=${encodeURIComponent(placeId.trim())}`;
    }
    return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
  };

  // ── Step 0 — Negocio ────────────────────────────────────────────────────────
  const saveNegocio = async () => {
    if (!negocio.name.trim()) { toast.error("El nombre es obligatorio"); return; }
    const addrErr = validateNormalizedAddress(geoAddress, { requireGeocoded: true });
    if (addrErr) { toast.error(addrErr); return; }
    const addressLine = addressLineFromGeo();
    if (!addressLine) { toast.error("La dirección es obligatoria"); return; }
    const lat = Number(geoAddress.latitude);
    const lng = Number(geoAddress.longitude);
    const mapsUrl = mapsUrlFromGeo(lat, lng, geoAddress.placeId);
    setBusy(true);
    try {
      let tid = tenantId;
      if (!tid) {
        const res = await fetch("/api/tenant/create", {
          method: "POST",
          credentials: "include",
          headers: { ...authHeader(), "Content-Type": "application/json" },
          body: JSON.stringify({ name: negocio.name.trim() }),
        });
        const data = (await res.json()) as { tenant?: { id: string; slug: string }; error?: string };
        if (!res.ok || !data.tenant) { toast.error(data.error ?? "No se pudo crear el negocio"); return; }
        tid = data.tenant.id;
        setTenantId(tid);
        setTenantSlug(data.tenant.slug);
        setStoredTenantId(tid);
        await refresh();
      }

      const patchRes = await fetch("/api/tenant", {
        method: "PATCH",
        credentials: "include",
        headers: tidHeader(tid),
        body: JSON.stringify({
          name: negocio.name.trim(),
          description: negocio.description.trim() || null,
          address: addressLine,
          onboardingStep: 1,
          latitude: lat,
          longitude: lng,
          placeId: geoAddress.placeId?.trim() || null,
          mapsUrl,
          branchType: negocio.branchType,
        }),
      });
      const patchData = (await patchRes.json()) as { tenant?: { slug?: string }; error?: string };
      if (!patchRes.ok) { toast.error(patchData.error ?? "Error"); return; }
      if (patchData.tenant?.slug) setTenantSlug(patchData.tenant.slug);

      await fetch("/api/tenant/config", {
        method: "PATCH",
        credentials: "include",
        headers: tidHeader(tid),
        body: JSON.stringify({
          business_profile: businessProfile,
          business_preset_id: businessPresetId,
          show_address_on_storefront: negocio.showAddressOnStorefront,
          whatsapp_number: negocio.whatsappNumber.trim() || null,
        }),
      });

      // Confirmar id de la sucursal principal creada/sincronizada
      const locsRes = await fetch("/api/locations", { credentials: "include", headers: tidHeader(tid) });
      if (locsRes.ok) {
        const locs = (await locsRes.json()) as Array<{ id: string; isDefault?: boolean }>;
        const primary = locs.find((l) => l.isDefault) ?? locs[0] ?? null;
        if (primary) setPrimaryLocationId(primary.id);
      }

      setSucursal((s) => ({
        ...s,
        phone: s.phone || negocio.whatsappNumber.trim(),
      }));
      toast.success("Negocio guardado — dirección lista como sucursal");
      setStep(1);
    } finally {
      setBusy(false);
    }
  };

  // ── Step 1 — Sucursal (nombre/teléfono/entrega; dirección ya viene del negocio)
  const saveSucursal = async () => {
    if (!sucursal.name.trim()) { toast.error("El nombre de la sucursal es obligatorio"); return; }
    const tid = tenantId;
    if (!tid) return;
    const addressLine = addressLineFromGeo();
    const lat = Number(geoAddress.latitude);
    const lng = Number(geoAddress.longitude);
    if (!addressLine || !Number.isFinite(lat) || !Number.isFinite(lng)) {
      toast.error("Volvé al paso Negocio y mapeá la dirección con Google Maps");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        name: sucursal.name.trim(),
        address: addressLine,
        phone: sucursal.phone.trim() || null,
        mapsUrl: mapsUrlFromGeo(lat, lng, geoAddress.placeId),
        isDefault: true,
        branchType: negocio.branchType,
        latitude: lat,
        longitude: lng,
        placeId: geoAddress.placeId?.trim() || null,
      };

      let locId = primaryLocationId;
      if (!locId) {
        const locsRes = await fetch("/api/locations", { credentials: "include", headers: tidHeader(tid) });
        if (locsRes.ok) {
          const locs = (await locsRes.json()) as Array<{ id: string; isDefault?: boolean }>;
          locId = (locs.find((l) => l.isDefault) ?? locs[0])?.id ?? null;
        }
      }

      if (locId) {
        const res = await fetch(`/api/locations?id=${encodeURIComponent(locId)}`, {
          method: "PATCH",
          credentials: "include",
          headers: tidHeader(tid),
          body: JSON.stringify(payload),
        });
        if (!res.ok) {
          const d = (await res.json()) as { error?: string };
          toast.error(d.error ?? "Error al actualizar sucursal");
          return;
        }
      } else {
        const res = await fetch("/api/locations", {
          method: "POST",
          credentials: "include",
          headers: tidHeader(tid),
          body: JSON.stringify(payload),
        });
        if (!res.ok) {
          const d = (await res.json()) as { error?: string };
          toast.error(d.error ?? "Error al crear sucursal");
          return;
        }
      }

      const metodoEntrega =
        sucursal.deliveryDomicilio && sucursal.deliveryRetiro
          ? "ambos"
          : sucursal.deliveryDomicilio
            ? "domicilio"
            : sucursal.deliveryRetiro
              ? "retiro"
              : "ambos";
      await fetch("/api/tenant/config", {
        method: "PATCH",
        headers: tidHeader(tid),
        body: JSON.stringify({ metodo_entrega: metodoEntrega }),
      });
      await fetch("/api/tenant", {
        method: "PATCH",
        headers: tidHeader(tid),
        body: JSON.stringify({ onboardingStep: 2 }),
      });
      toast.success("Sucursal lista");
      setStep(2);
    } finally {
      setBusy(false);
    }
  };

  // ── Step 2 — Categorías ─────────────────────────────────────────────────────
  const saveCategorias = async () => {
    const valid = categorias.filter((c) => c.name.trim());
    if (!valid.length) { toast.error("Agregá al menos una categoría"); return; }
    const tid = tenantId;
    if (!tid) return;
    setBusy(true);
    try {
      await Promise.all(
        valid.map((c) =>
          fetch("/api/categories", {
            method: "POST",
            headers: tidHeader(tid),
            body: JSON.stringify({ name: c.name.trim() }),
          }),
        ),
      );
      await fetch("/api/tenant", {
        method: "PATCH",
        headers: tidHeader(tid),
        body: JSON.stringify({ onboardingStep: 3 }),
      });
      toast.success("Categorías creadas");
      setStep(3);
    } finally {
      setBusy(false);
    }
  };

  // ── Step 3 — Productos ──────────────────────────────────────────────────────
  const saveProductos = async () => {
    const valid = productos.filter((p) => p.name.trim() && p.price);
    if (!valid.length) { toast.error("Agregá al menos un producto con nombre y precio"); return; }
    const tid = tenantId;
    if (!tid) return;
    setBusy(true);
    try {
      await Promise.all(
        valid.map((p) =>
          fetch("/api/products", {
            method: "POST",
            headers: tidHeader(tid),
            body: JSON.stringify({
              name:           p.name.trim(),
              description:    p.description.trim() || undefined,
              tipoVenta:      isDigitalOnboarding ? "unidad" : p.tipoVenta,
              precioUnitario: parseFloat(p.price),
              precioPorKg:    !isDigitalOnboarding && p.tipoVenta === "peso" ? parseFloat(p.price) : undefined,
              stock:          isDigitalOnboarding ? 999 : (parseFloat(p.stock) || 0),
              productType:    isDigitalOnboarding ? "digital" : "physical",
            }),
          }),
        ),
      );
      await fetch("/api/tenant", {
        method: "PATCH",
        headers: tidHeader(tid),
        body: JSON.stringify({ onboardingStep: 4 }),
      });
      toast.success("Productos creados");
      setStep(4);
    } finally {
      setBusy(false);
    }
  };

  // ── Step 4 — Pagos + Finalizar ──────────────────────────────────────────────
  const savePagosAndFinish = async () => {
    const tid = tenantId;
    if (!tid) return;
    setBusy(true);
    try {
      // Patch tenant config with payment settings
      await fetch("/api/tenant/config", {
        method: "PATCH",
        headers: tidHeader(tid),
        body: JSON.stringify({
          mp_public_key: pagos.mpPublicKey.trim() || null,
          mp_access_token: pagos.mpAccessToken.trim() || null,
          payment_methods: [
            ...(pagos.efectivo ? ["efectivo"] : []),
            ...(pagos.transferencia ? ["transferencia"] : []),
            ...(pagos.mpAccessToken.trim() ? ["mercadopago"] : []),
          ],
        }),
      });
      // Mark onboarding complete
      await fetch("/api/tenant", {
        method: "PATCH",
        headers: tidHeader(tid),
        body: JSON.stringify({ onboardingComplete: true, onboardingStep: 4 }),
      });
      await refresh();
      toast.success("¡Tu tienda está lista!");
      void navigate({ to: "/app/dashboard" });
    } finally {
      setBusy(false);
    }
  };

  if (!user) {
    return <PageLoader variant="screen" label="Cargando…" />;
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="flex h-[60px] items-center justify-center border-b border-border bg-background/95 backdrop-blur">
        <div className="flex items-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary">
            <ShoppingBasket className="h-4 w-4 text-primary-foreground" />
          </div>
          <span className="text-sm font-bold">
            Structura <span className="text-primary">Market</span>
          </span>
        </div>
      </header>

      <div className="mx-auto max-w-lg px-4 py-8">
        {/* Step pills */}
        <div className="mb-8 flex flex-wrap justify-center gap-2">
          {STEP_LABELS.map((_, i) => (
            <StepIndicator key={i} step={i} current={step} />
          ))}
        </div>

        <div className="rounded-2xl border border-border bg-card p-6 shadow-sm">

          {/* ── Step 0: Negocio ─────────────────────────────────────────────── */}
          {step === 0 && (
            <div className="space-y-4">
              <div>
                <h2 className="text-base font-semibold text-foreground">Contanos sobre tu negocio</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  Esta información aparecerá en tu tienda online.
                </p>
              </div>
              <div>
                <Label className="text-xs">Nombre comercial *</Label>
                <Input
                  className="mt-1"
                  value={negocio.name}
                  onChange={(e) => setNegocio({ ...negocio, name: e.target.value })}
                  placeholder="Ej. Almacén Don Pedro"
                />
              </div>
              <div>
                <Label className="text-xs">Tipo de negocio</Label>
                <select
                  className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
                  value={businessPresetId}
                  onChange={(e) => {
                    const id = e.target.value as FmBusinessPresetId;
                    setBusinessPresetId(id);
                    const preset = FM_BUSINESS_PRESETS.find((p) => p.id === id);
                    const profile = preset?.profile ?? "market";
                    const allowed = branchTypesForBusinessProfile(profile);
                    setNegocio((n) => ({
                      ...n,
                      branchType: allowed.includes(n.branchType) ? n.branchType : allowed[0] ?? "store",
                    }));
                    if (id === "digital_products") {
                      setSucursal((s) => ({ ...s, deliveryDomicilio: false, deliveryRetiro: true }));
                    }
                  }}
                >
                  {FM_BUSINESS_PRESETS.map((p) => (
                    <option key={p.id} value={p.id}>{p.label}</option>
                  ))}
                </select>
                {selectedPreset ? (
                  <p className="mt-1 text-[11px] text-muted-foreground">{selectedPreset.tagline}</p>
                ) : null}
              </div>
              <div>
                <Label className="text-xs">Descripción breve</Label>
                <Textarea
                  className="mt-1"
                  rows={2}
                  value={negocio.description}
                  onChange={(e) => setNegocio({ ...negocio, description: e.target.value })}
                  placeholder="¿Qué vendés? ¿Qué te hace especial?"
                />
              </div>
              <div>
                <Label className="text-xs">WhatsApp</Label>
                <Input
                  className="mt-1"
                  value={negocio.whatsappNumber}
                  onChange={(e) => setNegocio({ ...negocio, whatsappNumber: e.target.value })}
                  placeholder="+54 9 11 …"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Dirección *</Label>
                <p className="text-[11px] text-muted-foreground">
                  Obligatorio. Elegí la calle desde Google Maps (igual que en Sucursales).
                </p>
                <NormalizedAddressForm
                  value={geoAddress}
                  onChange={setGeoAddress}
                  variant="default"
                  showLabel={false}
                  showNotes={false}
                  requirePlacesSelection
                />
                {geoAddress.latitude != null && geoAddress.longitude != null ? (
                  <p className="text-[11px] text-emerald-600">
                    Ubicación OK · {Number(geoAddress.latitude).toFixed(5)}, {Number(geoAddress.longitude).toFixed(5)}
                  </p>
                ) : (
                  <p className="text-[11px] text-amber-600">Todavía sin coordenadas — buscá y elegí una sugerencia.</p>
                )}
                <div className="flex items-center justify-between gap-3 rounded-md border border-border px-3 py-2.5">
                  <div className="min-w-0">
                    <p className="text-sm font-medium">Mostrar dirección en la web</p>
                    <p className="text-[11px] text-muted-foreground">Podés dejarla oculta en la tienda pública.</p>
                  </div>
                  <Switch
                    checked={negocio.showAddressOnStorefront}
                    onCheckedChange={(v) => setNegocio({ ...negocio, showAddressOnStorefront: v })}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs">De qué se trata esta ubicación *</Label>
                <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3">
                  {allowedBranchTypes.map((type) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => setNegocio({ ...negocio, branchType: type })}
                      className={cn(
                        "flex flex-col items-start gap-0.5 rounded-md border p-2 text-left text-xs transition-all",
                        negocio.branchType === type
                          ? "border-primary bg-primary/5 font-medium"
                          : "border-border text-muted-foreground hover:border-muted-foreground/40",
                      )}
                    >
                      <span className="font-medium leading-tight">{BRANCH_TYPE_LABELS[type]}</span>
                      <span className="text-[10px] leading-tight opacity-70">{BRANCH_TYPE_DESCRIPTIONS[type]}</span>
                    </button>
                  ))}
                </div>
              </div>
              <Button className="w-full gap-2" disabled={busy} onClick={() => void saveNegocio()}>
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Continuar <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}

          {/* ── Step 1: Sucursal ────────────────────────────────────────────── */}
          {step === 1 && (
            <div className="space-y-4">
              <div>
                <h2 className="text-base font-semibold text-foreground">Tu primera sucursal</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  La dirección ya quedó cargada desde el negocio. Acá solo confirmás nombre, teléfono y entregas.
                </p>
                {addressLineFromGeo() ? (
                  <p className="mt-2 rounded-md border border-border/70 bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
                    <span className="font-medium text-foreground">Dirección: </span>
                    {addressLineFromGeo()}
                    {" · "}
                    {BRANCH_TYPE_LABELS[negocio.branchType]}
                  </p>
                ) : null}
              </div>
              <div>
                <Label className="text-xs">Nombre de la sucursal *</Label>
                <Input
                  className="mt-1"
                  value={sucursal.name}
                  onChange={(e) => setSucursal({ ...sucursal, name: e.target.value })}
                  placeholder="Ej. Local principal"
                />
              </div>
              <div>
                <Label className="text-xs">Teléfono de contacto</Label>
                <Input
                  className="mt-1"
                  value={sucursal.phone}
                  onChange={(e) => setSucursal({ ...sucursal, phone: e.target.value })}
                  placeholder="+54 9 11 …"
                />
              </div>
              <div>
                <Label className="text-xs mb-2 block">Tipos de entrega</Label>
                <div className="flex flex-wrap gap-3">
                  {(["deliveryDomicilio", "deliveryRetiro"] as const).map((key) => (
                    <label key={key} className="flex cursor-pointer items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={sucursal[key]}
                        onChange={(e) => setSucursal({ ...sucursal, [key]: e.target.checked })}
                        className="h-4 w-4 rounded border-border accent-primary"
                      />
                      {key === "deliveryDomicilio" ? "Envío a domicilio" : "Retiro en local"}
                    </label>
                  ))}
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="ghost" className="flex-1" onClick={() => setStep(0)}>Atrás</Button>
                <Button className="flex-1 gap-2" disabled={busy} onClick={() => void saveSucursal()}>
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  Continuar <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}

          {/* ── Step 2: Categorías ──────────────────────────────────────────── */}
          {step === 2 && (
            <div className="space-y-4">
              <div>
                <h2 className="text-base font-semibold text-foreground">Categorías de productos</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  Organizá tu catálogo. Ej: Lácteos, Verduras, Fiambres, Panificados.
                </p>
              </div>
              <div className="space-y-2">
                {categorias.map((cat, i) => (
                  <div key={i} className="flex gap-2">
                    <Input
                      className="flex-1"
                      value={cat.name}
                      onChange={(e) => {
                        const next = [...categorias];
                        next[i] = { name: e.target.value };
                        setCategorias(next);
                      }}
                      placeholder={`Categoría ${i + 1}`}
                    />
                    {categorias.length > 1 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="shrink-0 text-muted-foreground"
                        onClick={() => setCategorias(categorias.filter((_, j) => j !== i))}
                      >
                        ×
                      </Button>
                    )}
                  </div>
                ))}
              </div>
              {categorias.length < 8 && (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={() => setCategorias([...categorias, { name: "" }])}
                >
                  + Agregar categoría
                </Button>
              )}
              <div className="flex gap-2">
                <Button variant="ghost" className="flex-1" onClick={() => setStep(1)}>Atrás</Button>
                <Button className="flex-1 gap-2" disabled={busy} onClick={() => void saveCategorias()}>
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  Continuar <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}

          {/* ── Step 3: Productos ───────────────────────────────────────────── */}
          {step === 3 && (
            <div className="space-y-4">
              <div>
                <h2 className="text-base font-semibold text-foreground">
                  {isDigitalOnboarding ? "Tus primeros productos digitales" : "Tus primeros productos"}
                </h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  {isDigitalOnboarding
                    ? "Creá al menos un producto digital. Después podés subir archivos desde Productos digitales."
                    : "Agregá al menos uno. Podés agregar el catálogo completo desde el panel."}
                </p>
              </div>
              <div className="space-y-4">
                {productos.map((prod, i) => (
                  <div key={i} className="rounded-lg border border-border p-3 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-muted-foreground">Producto {i + 1}</span>
                      {productos.length > 1 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 px-2 text-xs text-muted-foreground"
                          onClick={() => setProductos(productos.filter((_, j) => j !== i))}
                        >
                          Eliminar
                        </Button>
                      )}
                    </div>
                    <div>
                      <Label className="text-xs">Nombre *</Label>
                      <Input
                        className="mt-1"
                        value={prod.name}
                        onChange={(e) => {
                          const next = [...productos];
                          next[i] = { ...next[i], name: e.target.value };
                          setProductos(next);
                        }}
                        placeholder={isDigitalOnboarding ? "Ej. Guía PDF de entrenamiento" : "Ej. Leche entera 1L"}
                      />
                    </div>
                    {!isDigitalOnboarding ? (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Tipo de venta</Label>
                        <select
                          className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
                          value={prod.tipoVenta}
                          onChange={(e) => {
                            const next = [...productos];
                            next[i] = { ...next[i], tipoVenta: e.target.value as "unidad" | "peso" };
                            setProductos(next);
                          }}
                        >
                          <option value="unidad">Por unidad</option>
                          <option value="peso">Por peso (kg)</option>
                        </select>
                      </div>
                      <div>
                        <Label className="text-xs">
                          {prod.tipoVenta === "peso" ? "Precio por kg (ARS)" : "Precio (ARS)"}
                        </Label>
                        <Input
                          className="mt-1"
                          type="number"
                          min="0"
                          step="0.01"
                          value={prod.price}
                          onChange={(e) => {
                            const next = [...productos];
                            next[i] = { ...next[i], price: e.target.value };
                            setProductos(next);
                          }}
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                    ) : (
                    <div>
                      <Label className="text-xs">Precio (ARS)</Label>
                      <Input
                        className="mt-1"
                        type="number"
                        min="0"
                        step="0.01"
                        value={prod.price}
                        onChange={(e) => {
                          const next = [...productos];
                          next[i] = { ...next[i], price: e.target.value };
                          setProductos(next);
                        }}
                        placeholder="0.00"
                      />
                    </div>
                    )}
                  </div>
                ))}
              </div>
              {productos.length < 5 && (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={() => setProductos([...productos, { name: "", description: "", price: "", tipoVenta: "unidad", stock: "0" }])}
                >
                  + Agregar producto
                </Button>
              )}
              <div className="flex gap-2">
                <Button variant="ghost" className="flex-1" onClick={() => setStep(2)}>Atrás</Button>
                <Button className="flex-1 gap-2" disabled={busy} onClick={() => void saveProductos()}>
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  Continuar <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}

          {/* ── Step 4: Pagos ───────────────────────────────────────────────── */}
          {step === 4 && (
            <div className="space-y-4">
              <div>
                <h2 className="text-base font-semibold text-foreground">Medios de pago</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  Configurá cómo querés cobrar. Podés activar MercadoPago después.
                </p>
              </div>

              {/* Efectivo + Transferencia */}
              <div>
                <Label className="text-xs mb-2 block">Pagos manuales</Label>
                <div className="space-y-2">
                  {(["efectivo", "transferencia"] as const).map((key) => (
                    <label key={key} className="flex cursor-pointer items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={pagos[key]}
                        onChange={(e) => setPagos({ ...pagos, [key]: e.target.checked })}
                        className="h-4 w-4 rounded border-border accent-primary"
                      />
                      {key === "efectivo" ? "Efectivo" : "Transferencia bancaria"}
                    </label>
                  ))}
                </div>
              </div>

              {/* MercadoPago */}
              <div className="rounded-lg border border-border bg-secondary/20 p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <div className="h-5 w-5 rounded bg-blue-600 flex items-center justify-center">
                    <span className="text-[10px] font-bold text-white">MP</span>
                  </div>
                  <span className="text-sm font-medium text-foreground">MercadoPago</span>
                  <span className="text-xs text-muted-foreground">(opcional)</span>
                  <HelpLink articleId="mercadopago-connect" label="Cómo conectar MP" variant="subtle" className="ml-auto" />
                </div>
                <div>
                  <Label className="text-xs">Public Key</Label>
                  <Input
                    className="mt-1 font-mono text-xs"
                    value={pagos.mpPublicKey}
                    onChange={(e) => setPagos({ ...pagos, mpPublicKey: e.target.value })}
                    placeholder="APP_USR-xxxxxxxx…"
                  />
                </div>
                <div>
                  <Label className="text-xs">Access Token</Label>
                  <Input
                    className="mt-1 font-mono text-xs"
                    type="password"
                    value={pagos.mpAccessToken}
                    onChange={(e) => setPagos({ ...pagos, mpAccessToken: e.target.value })}
                    placeholder="APP_USR-xxxxxxxx…"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Conseguís estas credenciales en{" "}
                  <a
                    href="https://www.mercadopago.com.ar/developers/es/docs/checkout-api/integration-configuration/checkout-bricks/payment-brick/default-rendering"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    mercadopago.com.ar/developers
                  </a>
                </p>
              </div>

              {/* Storefront preview */}
              {tenantSlug && (
                <div className="rounded-lg border border-primary/20 bg-primary/5 p-3 text-xs">
                  <p className="font-medium text-foreground mb-1">Tu tienda online estará en:</p>
                  <code className="text-primary break-all">/market/{tenantSlug}</code>
                </div>
              )}

              <div className="flex gap-2">
                <Button variant="ghost" className="flex-1" onClick={() => setStep(3)}>Atrás</Button>
                <Button className="flex-1 gap-2" disabled={busy} onClick={() => void savePagosAndFinish()}>
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  ¡Ir a mi panel!
                </Button>
              </div>
            </div>
          )}
        </div>

        <p className="mt-6 text-center text-xs text-muted-foreground">
          <Link to="/app/dashboard" className="text-primary hover:underline">
            Saltar al panel →
          </Link>
        </p>
      </div>
    </div>
  );
}
